/**
 * /admin/permissions — #14 P3 management screen.
 *
 * Two tabs:
 *   - Group defaults: a permission × group matrix; tick to grant a capability
 *     to a group, untick to revoke. System groups are flagged.
 *   - Per-user overrides: pick a user, see the inherited group baseline + the
 *     resolved effective set, and set each permission to Inherit / Allow / Deny.
 *
 * Gated on can('user.manage_permissions'); the backend also 403s. Changes apply
 * immediately and are audited (no maker-checker — resolved decision 3).
 */
import {
  Badge, Card, Checkbox, Container, Group, Loader, SegmentedControl,
  Select, Stack, Table, Tabs, Text, Title, Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconShieldLock } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiError } from '../api/client';
import { permissionsAdminApi } from '../api/permissionsAdmin';
import type {
  PermissionCatalogueItem, UserPermissionsView,
} from '../api/permissionsAdmin';
import { usePermissions } from '../hooks/usePermissions';
import { useUsers } from '../hooks/useUsers';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { BackToCompliance } from '../components/BackToCompliance';
import { AccessDeniedAlert } from '../components/RequireAccess';

const CATALOGUE_KEY = ['perm-admin', 'catalogue'] as const;
const GROUPS_KEY = ['perm-admin', 'groups'] as const;

type OverrideState = 'inherit' | 'allow' | 'deny';

export function PermissionsAdminPage() {
  useDocumentTitle('Permissions');
  const { can, isLoading } = usePermissions();

  if (isLoading) {
    return <Container size="xl"><Loader /></Container>;
  }
  if (!can('user.manage_permissions')) {
    return <AccessDeniedAlert />;
  }

  return (
    <Container size="xl">
      <Stack>
        <BackToCompliance to="/admin" label="Back to Admin" />
        <Group gap="xs"><IconShieldLock size={22} /><Title order={2}>Permissions</Title></Group>
        <Text c="dimmed">
          Group defaults are the baseline each role inherits; a per-user override
          adds or removes a single capability for one person (deny beats allow).
          Changes apply immediately and are recorded in the audit log.
        </Text>
        <Tabs defaultValue="groups">
          <Tabs.List>
            <Tabs.Tab value="groups">Group defaults</Tabs.Tab>
            <Tabs.Tab value="user">Per-user overrides</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="groups" pt="md"><GroupDefaultsPanel /></Tabs.Panel>
          <Tabs.Panel value="user" pt="md"><PerUserPanel /></Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  );
}

function PermKeyCell({ item }: { item: PermissionCatalogueItem }) {
  return (
    <Tooltip label={item.description ?? ''} disabled={!item.description} multiline w={280}>
      <Text size="sm" ff="monospace">{item.key}</Text>
    </Tooltip>
  );
}

function GroupDefaultsPanel() {
  const qc = useQueryClient();
  const catalogue = useQuery({ queryKey: CATALOGUE_KEY, queryFn: permissionsAdminApi.catalogue });
  const groups = useQuery({ queryKey: GROUPS_KEY, queryFn: permissionsAdminApi.groups });

  const mutate = useMutation({
    mutationFn: (v: { groupId: string; key: string; grant: boolean }) =>
      v.grant
        ? permissionsAdminApi.grantGroup(v.groupId, v.key)
        : permissionsAdminApi.revokeGroup(v.groupId, v.key),
    onSuccess: () => qc.invalidateQueries({ queryKey: GROUPS_KEY }),
    onError: (e: ApiError) =>
      notifications.show({ color: 'red', title: 'Could not update group default', message: e.detail ?? 'Error' }),
  });

  if (catalogue.isLoading || groups.isLoading) return <Loader />;
  if (!catalogue.data || !groups.data) return <Text c="red">Failed to load.</Text>;
  const grps = groups.data;

  return (
    <Card withBorder p="md" radius="md">
      <Table striped withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Permission</Table.Th>
            {grps.map((g) => (
              <Table.Th key={g.id} style={{ textAlign: 'center' }}>
                {g.name}
                {g.is_system && <Badge ml={4} size="xs" variant="light" color="gray">system</Badge>}
              </Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {catalogue.data.map((p) => (
            <Table.Tr key={p.key}>
              <Table.Td><PermKeyCell item={p} /></Table.Td>
              {grps.map((g) => (
                <Table.Td key={g.id} style={{ textAlign: 'center' }}>
                  <Checkbox
                    checked={g.permission_keys.includes(p.key)}
                    disabled={mutate.isPending}
                    onChange={(e) =>
                      mutate.mutate({ groupId: g.id, key: p.key, grant: e.currentTarget.checked })}
                  />
                </Table.Td>
              ))}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Card>
  );
}

function PerUserPanel() {
  const qc = useQueryClient();
  const [userId, setUserId] = useState<string | null>(null);
  const usersQ = useUsers({ limit: 200 });
  const catalogue = useQuery({ queryKey: CATALOGUE_KEY, queryFn: permissionsAdminApi.catalogue });
  const userKey = ['perm-admin', 'user', userId] as const;
  const userPerm = useQuery({
    queryKey: userKey,
    queryFn: () => permissionsAdminApi.userPermissions(userId!),
    enabled: !!userId,
  });

  const setOv = useMutation({
    mutationFn: (v: { key: string; value: OverrideState }) =>
      v.value === 'inherit'
        ? permissionsAdminApi.clearOverride(userId!, v.key)
        : permissionsAdminApi.setOverride(userId!, v.key, v.value === 'allow'),
    onSuccess: (data) => qc.setQueryData(userKey, data),
    onError: (e: ApiError) =>
      notifications.show({ color: 'red', title: 'Could not update override', message: e.detail ?? 'Error' }),
  });

  const userOptions = (usersQ.data?.items ?? []).map((u) => ({
    value: u.id, label: `${u.display_name} · ${u.role}`,
  }));

  return (
    <Stack>
      <Select
        label="User" placeholder="Pick a user" searchable
        data={userOptions} value={userId} onChange={setUserId} maw={360}
      />
      {userId && userPerm.isLoading && <Loader />}
      {userId && userPerm.data && catalogue.data && (
        <PerUserTable
          view={userPerm.data}
          catalogue={catalogue.data}
          pending={setOv.isPending}
          onChange={(key, value) => setOv.mutate({ key, value })}
        />
      )}
    </Stack>
  );
}

function PerUserTable({ view, catalogue, pending, onChange }: {
  view: UserPermissionsView;
  catalogue: PermissionCatalogueItem[];
  pending: boolean;
  onChange: (key: string, value: OverrideState) => void;
}) {
  const overrideOf = new Map(view.overrides.map((o) => [o.permission_key, o.allow]));
  const inherited = new Set(view.inherited);
  const effective = new Set(view.effective);

  return (
    <Card withBorder p="md" radius="md">
      <Text size="sm" c="dimmed" mb="xs">
        Group baseline: <b>{view.group_name ?? '—'}</b>. Effective = group default,
        then this user's override (deny beats allow).
      </Text>
      <Table striped withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Permission</Table.Th>
            <Table.Th style={{ textAlign: 'center' }}>Inherited</Table.Th>
            <Table.Th style={{ textAlign: 'center' }}>Override</Table.Th>
            <Table.Th style={{ textAlign: 'center' }}>Effective</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {catalogue.map((p) => {
            const ov: OverrideState = overrideOf.has(p.key)
              ? (overrideOf.get(p.key) ? 'allow' : 'deny')
              : 'inherit';
            return (
              <Table.Tr key={p.key}>
                <Table.Td><PermKeyCell item={p} /></Table.Td>
                <Table.Td style={{ textAlign: 'center' }}>
                  {inherited.has(p.key)
                    ? <Badge color="gray" variant="light">yes</Badge>
                    : <Text c="dimmed" size="sm">—</Text>}
                </Table.Td>
                <Table.Td style={{ textAlign: 'center' }}>
                  <SegmentedControl
                    size="xs" disabled={pending} value={ov}
                    onChange={(v) => onChange(p.key, v as OverrideState)}
                    data={[
                      { label: 'Inherit', value: 'inherit' },
                      { label: 'Allow', value: 'allow' },
                      { label: 'Deny', value: 'deny' },
                    ]}
                  />
                </Table.Td>
                <Table.Td style={{ textAlign: 'center' }}>
                  {effective.has(p.key)
                    ? <Badge color="green">can</Badge>
                    : <Badge color="red" variant="light">no</Badge>}
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </Card>
  );
}
